import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';

import { selectUserId } from '@entities/user';
import {
  selectGenerationState,
  setGenerationStateDetails,
  useBeginGenerationMutation,
} from '@entities/generation-state';
import { useAppDispatch, useAppSelector } from '@shared/model';
import { selectTemplates } from '@entities/template';
import { resetGenerationProgress, resetGenerationErrors } from '@entities/generation-state';
import {
  setGenerationStartedAt,
  setIsGenerationReportShown,
  setIsSendingReportShown,
} from '@pages/invoice-generator/shared/model';
import { selectIsSendingEmails } from '@entities/sender-state/model';

const useInvoiceGeneration = () => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);
  const templates = useAppSelector(selectTemplates);
  const { prefix, progress, excelFiles, isLoading } = useAppSelector(selectGenerationState);
  const isSending = useAppSelector(selectIsSendingEmails);

  const [text, setText] = useState('Generate');
  const [isReadyForGeneration, setIsReadyForGeneration] = useState(false);

  const [beginGeneration] = useBeginGenerationMutation();

  const beginGenerationAsync = async () => {
    dispatch(resetGenerationProgress());
    dispatch(resetGenerationErrors());
    dispatch(setIsSendingReportShown(false));

    const isNoTemplates = templates.length;

    if (!userId || isLoading || !isReadyForGeneration || !isNoTemplates) {
      return;
    }

    const dto = {
      prefix,
      userId,
    };

    try {
      const stateId = await beginGeneration(dto).unwrap();

      if (stateId) {
        dispatch(setGenerationStartedAt(new Date().getTime()));
        dispatch(setGenerationStateDetails(stateId));
        dispatch(setIsGenerationReportShown(true));
      }
    } catch {
      toast.error('Failed to begin generation. \n Please, try again later.', {
        style: {
          textAlign: 'center',
        },
      });
    }
  };

  useEffect(() => {
    if (isLoading && progress.total === 0) {
      setText('Preparing...');
      setIsReadyForGeneration(false);
    }

    if (isLoading && progress.total > 0) {
      setText(`Progress: ${progress.generated}/${progress.total}`);
    }

    if (!isLoading) {
      setText('Generate');
    }
  }, [progress, isLoading]);

  useEffect(() => {
    const { customerFileName, monthFileName, staffFileName } = excelFiles;
    const isReadyForGeneration =
      [prefix, customerFileName, monthFileName, staffFileName, !!templates.length].every(x => !!x === true) &&
      !isLoading;

    setIsReadyForGeneration(isReadyForGeneration);
  }, [prefix, excelFiles, isLoading, templates]);

  return {
    text,
    isLoading,
    isReadyForGeneration: isReadyForGeneration && !isSending,
    beginGenerationAsync,
  };
};

export default useInvoiceGeneration;
